import { Link } from "react-router-dom";
import { User, Sparkles, Users2, ArrowRight, ShieldCheck } from "lucide-react";

const services = [
    {
        icon: User,
        title: "Individual Therapy",
        body: "One-on-one sessions tailored to you — whether you're working through anxiety, a hard season, or simply want a quiet place to be understood.",
        tag: "Teens & Adults",
    },
    {
        icon: Sparkles,
        title: "Addiction & Relapse Therapy",
        body: "Focused support for substance use, early recovery, and relapse. We'll look at triggers, patterns, and build practical tools for the life you actually want.",
        tag: "Individual",
    },
    {
        icon: Users2,
        title: "Relapse Prevention Group",
        body: "A small, supportive group for sustained recovery. Real conversations with people walking a similar path — more details coming soon.",
        tag: "Opening Soon",
    },
];

const insuranceCarriers = ["Blue Cross Blue Shield", "Optum"];

export default function Services() {
    return (
        <div data-testid="services-page">
            {/* HEADER */}
            <section className="pt-20 pb-12 md:pt-28 md:pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl pd-fade-up">
                        <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                            Services
                        </p>
                        <h1 className="mt-5 font-heading text-3xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#2C332A] break-words">
                            Support that meets you{" "}
                            <span className="italic text-[#8C9C88]">
                                where you are.
                            </span>
                        </h1>
                        <p className="mt-8 text-lg text-[#5B6358] leading-relaxed">
                            Every person comes in carrying something different.
                            These are the ways Ray is here to help.
                        </p>
                    </div>
                </div>
            </section>

            {/* SERVICES GRID */}
            <section className="pb-20" data-testid="services-grid-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((s, i) => (
                            <article
                                key={s.title}
                                className="group rounded-2xl p-8 bg-[#F5F2EA] border border-[#E3DCC8] transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-white"
                                data-testid={`service-card-${i}`}
                            >
                                <div className="flex items-center justify-between">
                                    <s.icon className="w-8 h-8 text-[#8C9C88]" />
                                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#B5A38E] font-medium">
                                        {s.tag}
                                    </span>
                                </div>
                                <h3 className="mt-6 font-heading text-2xl text-[#2C332A] tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-4 text-[#5B6358] leading-relaxed">
                                    {s.body}
                                </p>
                            </article>
                        ))}
                    </div>

                    {/* PRICING & INSURANCE */}
                    <div
                        className="mt-10 rounded-2xl p-8 md:p-10 bg-white border border-[#E3DCC8]"
                        data-testid="services-pricing-note"
                    >
                        <div className="grid md:grid-cols-2 gap-10">
                            <div>
                                <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                                    Pricing
                                </p>
                                <h3 className="mt-3 font-heading text-2xl text-[#2C332A] tracking-tight">
                                    Determined by your coverage.
                                </h3>
                                <p className="mt-4 text-[#5B6358] leading-relaxed">
                                    Session fees are primarily determined by
                                    your insurance carrier and your specific
                                    plan benefits. For clients paying out of
                                    pocket, Ray's standard rate is{" "}
                                    <span className="text-[#2C332A] font-medium">
                                        $150 per 50-minute session.
                                    </span>
                                </p>
                                <p className="mt-4 text-[#5B6358] leading-relaxed">
                                    Unsure what applies to you? Book a free
                                    15-minute consultation and Ray will walk
                                    you through it.
                                </p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.35em] text-[#8C9C88] font-medium">
                                    Insurance accepted
                                </p>
                                <h3 className="mt-3 font-heading text-2xl text-[#2C332A] tracking-tight">
                                    In-network with:
                                </h3>
                                <ul
                                    className="mt-5 space-y-3"
                                    data-testid="services-insurance-list"
                                >
                                    {insuranceCarriers.map((name) => (
                                        <li
                                            key={name}
                                            className="flex items-center gap-3 rounded-xl bg-[#F5F2EA] border border-[#E3DCC8] px-4 py-3 text-[#2C332A] font-medium"
                                        >
                                            <ShieldCheck className="w-5 h-5 text-[#8C9C88]" />
                                            {name}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-4 text-sm text-[#5B6358] leading-relaxed">
                                    Coverage and copay vary by plan. Ray's
                                    office can help verify your benefits
                                    before your first session.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section
                className="py-24 bg-[#F5F2EA] pd-grain"
                data-testid="services-cta-section"
            >
                <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C332A]">
                        Ready to get started?
                    </h2>
                    <p className="mt-6 text-lg text-[#5B6358] leading-relaxed">
                        Book a consultation below — it's the simplest first
                        step.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/consultation"
                            className="inline-flex items-center gap-2 rounded-full bg-[#8C9C88] text-white px-8 py-3.5 font-medium shadow-sm transition-all duration-300 hover:bg-[#7A8A76] hover:-translate-y-0.5 hover:shadow-md"
                            data-testid="services-cta-book"
                        >
                            Book a Consultation
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
